import { useEffect, useMemo, useRef, useState } from "react";

import { usePage } from "contexts/PageContext";
import { useKeyboardList } from "hooks/useKeyboardList";

import { NavItem, useNavItems } from "../useNavItems";

import "./PageSearch.scss";

// Empty query keeps menu order; otherwise substring match on label/section
// with label-prefix matches ranked first.
function filterItems(items: NavItem[], query: string): NavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const matches = items.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.section.toLowerCase().includes(q)
  );
  matches.sort((a, b) => {
    const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
    const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.label.localeCompare(b.label);
  });
  return matches;
}

/**
 * Global page search in the navbar. Combobox over the same
 * permission-filtered page set the Sidebar shows.
 */
function PageSearch() {
  const { navigate } = usePage();
  const items = useNavItems();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => filterItems(items, query), [items, query]);

  const close = (): void => {
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const select = (item: NavItem | undefined): void => {
    if (!item) return;
    if (item.url) window.open(item.url, "_blank")?.focus();
    else navigate(item.action);
    close();
  };

  const kb = useKeyboardList({
    itemCount: open ? results.length : 0,
    onSubmit: (i) => select(results[i]),
    onClear: close,
    resetSignal: `${open}::${query}`,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="wb-page-search" ref={containerRef}>
      <i className="bi bi-search wb-page-search__icon" aria-hidden />
      <input
        ref={inputRef}
        className="wb-page-search__input"
        type="text"
        value={query}
        placeholder="Search pages…"
        autoComplete="off"
        aria-label="Search pages"
        role="combobox"
        aria-expanded={open}
        aria-controls="wb-page-search-list"
        aria-activedescendant={
          open && results[kb.activeIndex]
            ? `wb-page-search-opt-${kb.activeIndex}`
            : undefined
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={kb.onInputKeyDown}
      />
      {open && (
        <div
          className="wb-page-search__list"
          id="wb-page-search-list"
          role="listbox"
          aria-label="Pages"
          ref={kb.listRef}
        >
          {results.length === 0 && (
            <div className="wb-page-search__empty">No matches</div>
          )}
          {results.map((item, i) => (
            <button
              key={item.id}
              type="button"
              id={`wb-page-search-opt-${i}`}
              className={`wb-page-search__opt${
                kb.isActive(i) ? " is-active" : ""
              }`}
              role="option"
              aria-selected={kb.isActive(i)}
              data-kb-index={i}
              onMouseEnter={() => kb.setActiveIndex(i)}
              onClick={() => select(item)}
            >
              <span className="wb-page-search__label">{item.label}</span>
              <span className="wb-page-search__section">{item.section}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PageSearch;
